import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { History, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export type TimeRangeMode = 'historical' | 'forecast';
export type HistoricalRange = '3' | '5' | '10';
export type ForecastRange = '5' | '10' | '20';

interface TimeRangeSelectorProps {
  mode: TimeRangeMode;
  onModeChange: (mode: TimeRangeMode) => void;
  historicalRange: HistoricalRange;
  onHistoricalRangeChange: (range: HistoricalRange) => void;
  forecastRange: ForecastRange;
  onForecastRangeChange: (range: ForecastRange) => void;
  compact?: boolean;
}

// Button-based range selector that doesn't break on interaction
function RangeButton({
  label,
  isSelected,
  onClick,
  compact = false,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <Button
      type="button"
      variant={isSelected ? 'default' : 'outline'}
      size="sm"
      className={cn(
        compact ? 'flex-1 min-w-[60px] px-2 py-1.5 text-xs h-8' : 'px-3 py-1.5 text-sm',
        isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90'
      )}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

export function TimeRangeSelector({
  mode,
  onModeChange,
  historicalRange,
  onHistoricalRangeChange,
  forecastRange,
  onForecastRangeChange,
  compact = false,
}: TimeRangeSelectorProps) {
  // Compact mode - just show range buttons
  if (compact) {
    return (
      <div className="space-y-2">
        {mode === 'historical' ? (
          <div className="flex flex-wrap gap-1">
            <RangeButton
              label="3 yr"
              isSelected={historicalRange === '3'}
              onClick={() => onHistoricalRangeChange('3')}
              compact
            />
            <RangeButton
              label="5 yr"
              isSelected={historicalRange === '5'}
              onClick={() => onHistoricalRangeChange('5')}
              compact
            />
            <RangeButton
              label="10 yr"
              isSelected={historicalRange === '10'}
              onClick={() => onHistoricalRangeChange('10')}
              compact
            />
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            <RangeButton
              label="5 yr"
              isSelected={forecastRange === '5'}
              onClick={() => onForecastRangeChange('5')}
              compact
            />
            <RangeButton
              label="10 yr"
              isSelected={forecastRange === '10'}
              onClick={() => onForecastRangeChange('10')}
              compact
            />
            <RangeButton
              label="20 yr"
              isSelected={forecastRange === '20'}
              onClick={() => onForecastRangeChange('20')}
              compact
            />
          </div>
        )}
      </div>
    );
  }

  // Default mode - full layout
  return (
    <div className="space-y-3">
      {/* Mode Toggle */}
      <Tabs value={mode} onValueChange={(v) => onModeChange(v as TimeRangeMode)}>
        <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-grid">
          <TabsTrigger value="historical" className="gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Historical</span>
            <span className="sm:hidden">Past</span>
          </TabsTrigger>
          <TabsTrigger value="forecast" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Forecast</span>
            <span className="sm:hidden">Future</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Range Selection */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {mode === 'historical' ? 'Look back:' : 'Project:'}
        </span>

        {mode === 'historical' ? (
          <div className="flex gap-1">
            <RangeButton
              label="3 Years"
              isSelected={historicalRange === '3'}
              onClick={() => onHistoricalRangeChange('3')}
            />
            <RangeButton
              label="5 Years"
              isSelected={historicalRange === '5'}
              onClick={() => onHistoricalRangeChange('5')}
            />
            <RangeButton
              label="10 Years"
              isSelected={historicalRange === '10'}
              onClick={() => onHistoricalRangeChange('10')}
            />
          </div>
        ) : (
          <div className="flex gap-1">
            <RangeButton
              label="5 Years"
              isSelected={forecastRange === '5'}
              onClick={() => onForecastRangeChange('5')}
            />
            <RangeButton
              label="10 Years"
              isSelected={forecastRange === '10'}
              onClick={() => onForecastRangeChange('10')}
            />
            <RangeButton
              label="20 Years"
              isSelected={forecastRange === '20'}
              onClick={() => onForecastRangeChange('20')}
            />
          </div>
        )}
      </div>
    </div>
  );
}
